import { cn } from '@heidi/utils';
import { AlertCircle, AlertTriangle, CheckCircle2, X } from 'lucide-react';
import * as React from 'react';
import { toast as sonnerToast, Toaster as SonnerToaster } from 'sonner';
import { Button } from '../button';
import { ToastAnalyticsContext } from '../contexts/toast-analytics';
import { useDesignSystemIntl } from '../hooks/use-design-system-intl';
import { useIsExperimentalDesignSystemForms } from '../hooks/use-is-experimental-design-system-forms';
import { TierPracticeFilled, TierProFilled } from '../icons';
import { TypographyCaption, TypographyP2Bold } from '../typography';
import { getToastToneClassesExperimental } from './toast.experimental.styles';
import { getToastBaseClasses, getToastToneClasses } from './toast.styles';
import type { ToasterProps, ToastOptions, ToastProps, ToastTone } from './toast.types';

const getToneIcon = (tone: ToastTone) => {
  switch (tone) {
    case 'success':
      return <CheckCircle2 />;
    case 'destructive':
      return <AlertTriangle />;
    case 'warning':
      return <AlertCircle />;
    case 'pro':
      return <TierProFilled />;
    case 'practice':
      return <TierPracticeFilled />;
    default:
      return <CheckCircle2 />;
  }
};

const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      'data-testid': testId,
      className,
      title,
      description,
      variant = 'outline',
      tone = 'default',
      icon,
      action,
      onDismiss,
      ...props
    },
    ref
  ) => {
    const isOutline = variant === 'outline';
    const displayIcon = icon ?? getToneIcon(tone);
    const isExperimentalForms = useIsExperimentalDesignSystemForms();
    const toneClasses = isExperimentalForms
      ? getToastToneClassesExperimental
      : getToastToneClasses;
    const intl = useDesignSystemIntl();
    const closeLabel = intl.formatMessage({
      defaultMessage: 'Close',
      id: 'D4HAV3JkxH',
      description: 'Default accessible label for the Toast close button',
    });

    // Error-class toasts report to the host app's telemetry via the injected
    // callback (see `ToastAnalyticsContext`) — the design system stays
    // analytics-agnostic. Fire once, when the toast is shown (on mount): sonner
    // mounts one `Toast` per `toast()` call, so this matches the previous
    // "fire inside toast()" behaviour without coupling the package to analytics.
    const { onErrorToast } = React.useContext(ToastAnalyticsContext);
    React.useEffect(() => {
      if (tone !== 'destructive' && tone !== 'warning') return;
      const message =
        typeof description === 'string'
          ? description
          : typeof title === 'string'
            ? title
            : 'Unknown error';
      onErrorToast?.({ message, tone });
      // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per shown toast (mount)
    }, []);

    return (
      <div
        ref={ref}
        data-testid={testId}
        data-experimental-form={isExperimentalForms}
        className={cn(
          getToastBaseClasses(variant),
          toneClasses(variant, tone),
          className
        )}
        {...props}
      >
        <div className="flex flex-1 items-start gap-2">
          <div className="flex shrink-0 items-center pt-[3px] [&>svg]:size-4 [&>svg]:shrink-0">
            {displayIcon}
          </div>
          <div className="flex flex-1 flex-col gap-0.5">
            {title && <TypographyP2Bold>{title}</TypographyP2Bold>}
            {description && (
              <TypographyCaption
                className={cn(
                  isOutline
                    ? isExperimentalForms
                      ? 'text-notifications-text-description'
                      : 'text-text-secondary'
                    : 'opacity-90'
                )}
              >
                {description}
              </TypographyCaption>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {action}
          {onDismiss && (
            <Button
              variant="ghost"
              size="xxs"
              iconOnly
              flush="all"
              onClick={onDismiss}
              aria-label={closeLabel}
              className={cn(
                'opacity-50 hover:bg-transparent hover:opacity-100 hover:shadow-none active:bg-transparent active:opacity-100',
                isOutline
                  ? 'text-text-secondary hover:text-text-secondary active:text-text-secondary'
                  : 'text-current hover:text-current active:text-current'
              )}
            >
              <X />
            </Button>
          )}
        </div>
      </div>
    );
  }
);
Toast.displayName = 'Toast';

/**
 * Helper function to show a toast using Sonner
 * @returns The toast ID for programmatic dismissal
 */
const toast = ({
  title,
  description,
  variant = 'outline',
  tone = 'default',
  icon,
  action,
  closeButton = true,
  duration = 5000,
  className,
  position,
  'data-testid': testId,
}: ToastOptions): string | number => {
  return sonnerToast.custom(
    (toastId) => (
      <Toast
        title={title}
        description={description}
        variant={variant}
        tone={tone}
        icon={icon}
        action={action}
        onDismiss={closeButton ? () => sonnerToast.dismiss(toastId) : undefined}
        className={className}
        data-testid={testId}
      />
    ),
    { duration, position }
  );
};

/**
 * Toaster component that renders Sonner toasts
 * Add this once at the root of your app
 */
const Toaster = ({
  expand = true,
  visibleToasts = 3,
  gap = 8,
  position = 'bottom-center',
  offset = '24px',
  ...props
}: ToasterProps) => {
  return (
    <SonnerToaster
      position={position}
      expand={expand}
      visibleToasts={visibleToasts}
      gap={gap}
      offset={offset}
      {...props}
    />
  );
};

export { toast, Toast, Toaster };
